import {
    ResponsiveContainer,
    BarChart,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Bar,
    Rectangle
} from 'recharts';
import clsx from 'clsx';
import useTranslation from 'next-translate/useTranslation';
import { useMemo } from 'react';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';

//hook
import useDarkMode from '@/hooks/utils/useDarkMode';
import useFormatNumber from '@/hooks/format/useFormatNumber';

// Components
import TooltipCustom from './components/TooltipCustom';
import ChartWrapper from './ChartWrapper';

// Lib
import { darkModeColorsChart } from '@/lib/analytics/colors';

const DynamicTableChart = dynamic(() => import('./components/TableChart'));

const CustomYAxisTick = ({ x, y, payload, isExport }) => {
    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={0}
                y={0}
                dy={3}
                textAnchor="end"
                className={clsx('fill-current font-GSemiBold text-xxs text-icoGray-500', {
                    'dark:text-icoDarkMode-wolf': !isExport
                })}>
                {payload.value}
            </text>
        </g>
    );
};

CustomYAxisTick.propTypes = {
    x: PropTypes.number,
    y: PropTypes.number,
    payload: PropTypes.object,
    isExport: PropTypes.bool
};

// eslint-disable-next-line react/display-name
const SimpleHorizontalBarChartComponent = ({
    data,
    activeTable,
    userNumbersFormat,
    isExport,
    height = 260
}) => {
    const { isDarkMode } = useDarkMode();
    const theme = isDarkMode() && !isExport ? 'dark' : 'light';
    const { t } = useTranslation('analytics');
    const { formatNumber } = useFormatNumber(userNumbersFormat?.number);

    const finalColumnNames = useMemo(() => {
        let obj = {};

        if (data.graphData.length) {
            Object.entries(data.graphData[0])?.map((item) => {
                obj[item[0]] = t(data.name[item[0]]);
            });
        }

        return obj;
    }, [data.name, data.graphData, t]);

    return (
        <>
            {activeTable ? (
                <DynamicTableChart
                    isExport={isExport}
                    graphData={data.graphData}
                    columnNames={finalColumnNames}
                    userNumbersFormat={userNumbersFormat}
                    valueFormat={data.valueFormat.notation !== 'compact' ? data.valueFormat : {}}
                    timeFormat={data.timeFormatter}
                    defaultTableSorting={data?.defaultTableSorting ?? null}
                />
            ) : (
                <div
                    className="relative h-full w-full"
                    style={{ minHeight: isExport ? 180 : height }}>
                    <div className="absolute left-0 top-0 h-full w-full">
                        <ResponsiveContainer
                            height={isExport ? 180 : height}
                            width={isExport ? data.fixedSize : '100%'}>
                            <BarChart
                                data={data.graphData}
                                margin={data.margin}
                                barSize={10}
                                layout="vertical">
                                <CartesianGrid
                                    horizontal={false}
                                    stroke={darkModeColorsChart[theme].grid}
                                />
                                <XAxis
                                    dataKey={Object.keys(data.name)[0]}
                                    tickFormatter={(item) => formatNumber(item, data.valueFormat)}
                                    type="number"
                                    tickMargin={10}
                                    tickLine={false}
                                    tickCount={12}
                                    axisLine={false}
                                />
                                <YAxis
                                    dataKey={Object.keys(data.name)[1]}
                                    type="category"
                                    tick={<CustomYAxisTick />}
                                    interval={0}
                                    tickLine={false}
                                    axisLine={false}
                                />
                                <Tooltip
                                    cursor={false}
                                    content={
                                        <TooltipCustom
                                            tooltipTranslation={data.tooltipTranslation}
                                            valueFormat={
                                                data.valueFormat.notation !== 'compact'
                                                    ? data.valueFormat
                                                    : {}
                                            }
                                            data={data}
                                            dateFormat={data.dateFormatter}
                                        />
                                    }
                                />
                                <Bar
                                    isAnimationActive={!isExport}
                                    fill={darkModeColorsChart[theme][data.color.value]}
                                    dataKey="value"
                                    shape={(props) => {
                                        props.radius = [0, 2.5, 2.5, 0];

                                        return <Rectangle {...props} />;
                                    }}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}
        </>
    );
};

SimpleHorizontalBarChartComponent.propTypes = {
    data: PropTypes.object,
    columnNames: PropTypes.object,
    activeTable: PropTypes.bool,
    height: PropTypes.number,
    userNumbersFormat: PropTypes.object
};

const SimpleHorizontalBarChart = ({
    data,
    onReset = () => {
        /** */
    },
    isLoading = false,
    className = '',
    ...restProps
}) => (
    <ChartWrapper
        className={className}
        isLoading={isLoading}
        onReset={onReset}
        data={data}
        legendType="square"
        {...restProps}>
        <SimpleHorizontalBarChartComponent />
    </ChartWrapper>
);

SimpleHorizontalBarChart.propTypes = {
    data: PropTypes.object,
    className: PropTypes.string,
    onReset: PropTypes.func,
    isLoading: PropTypes.bool
};

export { SimpleHorizontalBarChart, SimpleHorizontalBarChartComponent };
